import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

// import components
import GenericLink from '../generic/Generic.link';
import NavigationToggle from './Navigation.toggle';

const NavigationGrid = () => {
  const [isOpen, setOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  const [openDropdown, setOpenDropdown] = React.useState(null);
  const itemRefs = React.useRef([]);
  const buttonRefs = React.useRef([]);
  const dropdownIdPrefix = React.useId();
  const location = useLocation();
  const links = useSelector((state) => state.app.navigation || []);

  const normalisePath = (path = '') => {
    const pathname = `${path}`.split(/[?#]/)[0];
    const trimmedPath = pathname.replace(/^\/+|\/+$/g, '');

    return trimmedPath === '' ? '/' : `/${trimmedPath}`;
  };

  const isInternalPageActive = (page = '') => {
    if (!page) {
      return false;
    }

    const currentPath = normalisePath(location.pathname);
    const linkPath = normalisePath(page);

    return currentPath === linkPath || currentPath.startsWith(`${linkPath}/`);
  };

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleViewportChange = (event) => {
      setIsMobile(event.matches);
      setOpenDropdown(null);
      setOpen(false);
      document.body.style.overflow = 'auto';
    };

    mediaQuery.addEventListener('change', handleViewportChange);
    return () => mediaQuery.removeEventListener('change', handleViewportChange);
  }, []);

  React.useEffect(() => {
    if (openDropdown === null) {
      return;
    }

    const handleOutsideClick = (event) => {
      if (!itemRefs.current[openDropdown]?.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('pointerdown', handleOutsideClick);
    return () => document.removeEventListener('pointerdown', handleOutsideClick);
  }, [openDropdown]);

  React.useEffect(() => {
    setOpenDropdown(null);
  }, [links]);

  React.useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const closeNavigation = () => {
    setOpenDropdown(null);
    if (isMobile) {
      setOpen(false);
      document.body.style.overflow = 'auto';
    }
  };

  const renderLinks = () => {
    const itemWidth = links?.length > 0 ? 100 / links?.length : 100;

    return links.map((link, index) => {
      const children = link.children || [];
      const hasChildren = children?.length > 0;
      const dropdownOnly = link.link_type === 'dropdown_only';
      const dropdownOpen = !isMobile && openDropdown === index;
      const parentActive =
        link.link_type === 'internal_page' && isInternalPageActive(link.page)
          ? true
          : children.some(
              (childLink) => childLink.link_type === 'internal_page' && isInternalPageActive(childLink.page)
            );
      const parentLinkClassName = `navigation__link${parentActive ? ' active' : ''}`;
      const dropdownId = `${dropdownIdPrefix}-dropdown-${index}`;
      const itemClassName =
        'navigation__item' +
        (hasChildren ? ' navigation__item--has-children' : '') +
        (dropdownOnly ? ' navigation__item--dropdown-only' : '') +
        (dropdownOnly && dropdownOpen ? ' navigation__item--dropdown-open' : '') +
        (link.dropdown_key ? ` navigation__item--${link.dropdown_key}` : '');

      return (
        <li
          key={index}
          ref={(element) => (itemRefs.current[index] = element)}
          className={itemClassName}
          style={{ width: `${itemWidth}%` }}
          onPointerEnter={(event) => {
            if (dropdownOnly && event.pointerType === 'mouse' && !isMobile) {
              setOpenDropdown(index);
            }
          }}
          onPointerLeave={(event) => {
            if (
              dropdownOnly &&
              event.pointerType === 'mouse' &&
              !isMobile &&
              !event.currentTarget.contains(document.activeElement)
            ) {
              setOpenDropdown((current) => (current === index ? null : current));
            }
          }}
          onBlur={(event) => {
            if (dropdownOnly && !event.currentTarget.contains(event.relatedTarget)) {
              setOpenDropdown((current) => (current === index ? null : current));
            }
          }}
          onKeyDown={(event) => {
            if (dropdownOnly && dropdownOpen && event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              setOpenDropdown(null);
              buttonRefs.current[index]?.focus();
            }
          }}
        >
          {dropdownOnly && isMobile ? (
            <span className={parentLinkClassName}>{link.text}</span>
          ) : dropdownOnly ? (
            <button
              ref={(element) => (buttonRefs.current[index] = element)}
              type="button"
              className={`${parentLinkClassName} navigation__trigger`}
              aria-expanded={dropdownOpen}
              aria-controls={dropdownId}
              onClick={() => setOpenDropdown((current) => (current === index ? null : index))}
            >
              {link.text}
            </button>
          ) : (
            <GenericLink data={{ ...link }} navType={'nav'} className={parentLinkClassName} onClick={closeNavigation} />
          )}
          {hasChildren && (
            <ul
              id={dropdownId}
              className="navigation__dropdown"
              aria-label={`${link.text} menu`}
              hidden={dropdownOnly && !isMobile && !dropdownOpen}
            >
              {children.map((childLink, childIndex) => (
                <li key={childIndex} className="navigation__dropdown-item">
                  <GenericLink
                    data={{ ...childLink }}
                    navType={'nav'}
                    className="navigation__dropdown-link"
                    onClick={closeNavigation}
                  />
                </li>
              ))}
            </ul>
          )}
        </li>
      );
    });
  };

  const handleToggle = () => {
    setOpenDropdown(null);
    if (!isMobile) {
      setOpen(false);
    } else {
      setOpen(!isOpen);
      document.body.style.overflow = !isOpen ? 'hidden' : 'auto';
    }
  };

  return (
    <>
      <button className="navigation__toggle" onClick={handleToggle} aria-label="Toggle navigation menu">
        <NavigationToggle
          isOpen={isOpen}
          strokeWidth="8"
          color="#0092D4"
          lineProps={{ strokeLinecap: 'round' }}
          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          width="24"
          height="24"
          style={{ width: 'auto' }}
        />
      </button>

      <nav className={'navigation' + (isOpen ? ' navigation--open' : ' navigation--close')}>
        <ul className="navigation__grid">{renderLinks()}</ul>
      </nav>
    </>
  );
};

export default NavigationGrid;
